import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BlueprintInstanceType, Components, Island, Panel, PanelItemGroup } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { renderTracking } from '@r-services/render-tracking';
import {
	getLabelColor,
	getLabelText,
	getOverheadGroupPosition,
	stackLabelsGlobally,
	transformToGlobal,
	type GlobalSkuLabelData
} from '../helpers/sku-labels';

// Visual gap in inches between the panel outline outer edge and the nearest
// edge of a TOP_ONLY SKU label box (issue #249).
const TOP_ONLY_LABEL_PANEL_GAP = 0.5;

export type RenderAllSkuLabelsOverheadProps = {
	islands: Island[];
};

/**
 * RenderAllSkuLabelsOverhead Component
 * Renders all SKU labels for all islands in GLOBAL coordinates.
 * Labels are always on top of all islands and stack vertically in screen space regardless of island rotation.
 */
export const RenderAllSkuLabelsOverhead = ({ islands }: RenderAllSkuLabelsOverheadProps): React.ReactElement => {
	const { controller } = useContext(AppContext);
	const { kitchen, options } = controller;

	const fontSizeFactor = options?.dimensionFontSizeFactor ?? 1;
	const [colorVersion, setColorVersion] = useState(0);

	renderTracking.incrementRender('RenderAllSkuLabelsOverhead');

	useEffect(() => {
		const subscriptions: string[] = [];

		const subscriptionEvents = [
			['item_sku_color', '*'],
			['item_sku', 'updated'],
			['component_custom_label', 'updated'],
			['component_sku', 'updated'],
			['sku-label-color', '*'],
			['panel_item', 'deleted'],
			['panel_items', 'add'],
			['panel_item', 'moved'],
			['panel_item_group', '*'],
			['panel', '*'],
			['island', '*'],
			['connection', '*']
		];

		subscriptionEvents.forEach(([noun, action]) => {
			const source = noun === 'sku-label-color' ? controller : kitchen;
			subscriptions.push(source.subscribe(noun, action, () => {
				setColorVersion((v) => v + 1);
			}));
		});

		return () => {
			subscriptions.forEach((subId) => {
				kitchen.unsubscribe(subId);
				controller.unsubscribe(subId);
			});
		};
	}, [kitchen, controller]);

	const LABEL_FONT_SIZE = DIMENSION_FONT_SIZE * fontSizeFactor;
	const LABEL_PADDING_X = 0.5 * fontSizeFactor;
	const LABEL_PADDING_Y = 0.25 * fontSizeFactor;
	const LABEL_BORDER_RADIUS = 0.25 * fontSizeFactor;

	const globalSkuLabels = useMemo<GlobalSkuLabelData[]>(() => {
		// Sits the label BOX above the panel outer edge (issue #249). Independent
		// of countertop thickness and driven only by panel.size.depth -- gap in
		// inches between the panel outline outer edge and the label box.
		const halfLabelBoxHeight = (DIMENSION_FONT_SIZE * fontSizeFactor + 2 * 0.25 * fontSizeFactor) / 2;
		const labelsByGroup = new Map<string, GlobalSkuLabelData[]>();

		islands.forEach((island) => {
			const islandCenterX = island.center.x;
			const islandCenterZ = island.center.z;
			const islandRotation = island.axis?.y ?? 0;

			island.panels.children.forEach((panel: Panel) => {
				panel.panelItemGroups.children.forEach((panelItemGroup: PanelItemGroup) => {
					if (panelItemGroup.hasPassthrough) return;

					const localPosition = getOverheadGroupPosition(panelItemGroup, panel);
					const globalPosition = transformToGlobal(
						localPosition.x,
						localPosition.z,
						islandCenterX,
						islandCenterZ,
						islandRotation
					);

					// TOP_ONLY appliances (drop-in sinks, cooktops, etc.) live on the
					// countertop, not inside a cutout, so the group-stack push floats
					// their label far off the panel outline in mfg view. Pin the label
					// BOX just above the panel outer edge -- half depth to reach the
					// outer face, the box's half-height to sit above it, plus a small
					// visual gap -- see issue #249. No countertop-thickness input.
					const outerEdgeOffset = panel.size.depth / 2 + halfLabelBoxHeight + TOP_ONLY_LABEL_PANEL_GAP;
					const outerEdgeLocal = getOverheadGroupPosition(panelItemGroup, panel, { panelLocalZ: outerEdgeOffset });
					const outerEdgeGlobal = transformToGlobal(
						outerEdgeLocal.x,
						outerEdgeLocal.z,
						islandCenterX,
						islandCenterZ,
						islandRotation
					);

					const groupKey = `${island.id}-${panelItemGroup.id}`;
					const groupLabels: GlobalSkuLabelData[] = [];

					panelItemGroup.items.children.forEach((panelItem) => {
						if (!panelItem.item.showInSkuOverhead) return;
						if (panelItem.item.skuOverheadMode === 'image') return;
						if ((panelItem.item.sku === 'CUSTOM' || panelItem.item.sku === 'CUTOUT') && panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) return;

						const labelText = getLabelText(panelItem);
						const labelColor = getLabelColor(
							panelItem,
							panelItem.item.skuColor ?? undefined
						);

						const isTopOnly = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;

						groupLabels.push({
							id: panelItem.id,
							text: labelText,
							color: labelColor,
							globalX: isTopOnly ? outerEdgeGlobal.x : globalPosition.x,
							globalZ: isTopOnly ? outerEdgeGlobal.z : globalPosition.z,
							groupKey,
							verticalPosition: panelItem.center.y,
							panelItem,
							panelItemGroup,
							panelId: panel.id,
							islandId: island.id,
							pinPosition: isTopOnly
						});
					});

					if (groupLabels.length > 0) {
						labelsByGroup.set(groupKey, groupLabels);
					}
				});
			});
		});

		const stacked = stackLabelsGlobally(labelsByGroup, fontSizeFactor);

		return stacked;
	}, [islands, colorVersion, fontSizeFactor]);

	return (
		<g type="all-sku-labels-overhead">
			{globalSkuLabels.map((labelData) => {
				const estimatedTextWidth = labelData.text.length * LABEL_FONT_SIZE * 0.6;
				const rectWidth = estimatedTextWidth + (LABEL_PADDING_X * 2);
				const rectHeight = LABEL_FONT_SIZE + (LABEL_PADDING_Y * 2);

				const contextMenuProps = addContextMenu({
					menuKey: BlueprintInstanceType.PanelItem,
					id: labelData.id,
					center: { x: labelData.globalX, y: labelData.globalZ }
				});

				return (
					<g
						key={labelData.id}
						transform={`translate(${labelData.globalX}, ${labelData.globalZ})`}
						style={{ pointerEvents: 'auto', cursor: 'context-menu' }}
						data-panel-item-id={labelData.id}
						className="sku-label-group"
						{...contextMenuProps}
					>
						<rect
							x={-rectWidth / 2}
							y={-rectHeight / 2}
							width={rectWidth}
							height={rectHeight}
							rx={LABEL_BORDER_RADIUS}
							ry={LABEL_BORDER_RADIUS}
							fill={labelData.color}
							stroke="#333333"
							strokeWidth={0.05}
							opacity={0.95}
						/>
						<text
							x={0}
							y={0}
							textAnchor="middle"
							dominantBaseline="central"
							fontSize={LABEL_FONT_SIZE}
							fontFamily="Arial, sans-serif"
							fontWeight="500"
							fill="#333333"
							className="rta-designer-component-label rta-noselect"
						>
							{labelData.text}
						</text>
					</g>
				);
			})}
		</g>
	);
};
